import Big from 'big.js'
import TokenBalance from 'src/common/components/TokenBalance/TokenBalance'
import TokenSelector from 'src/common/components/TokenSelector/TokenSelector'
import { useAllPriceFeeds } from 'src/common/queries/usePriceFeeds/usePriceFeeds'
import { getPriceFromPriceFeed } from 'src/common/queries/usePriceFeeds/utils'
import { AnyToken } from 'src/common/types/models/Tokens'
import NumericField from 'src/common/ui-kit/NumericField/NumericField'
import { displayAmount } from 'src/common/utils/formatters/number'
import TokenAmountController from './TokenAmountController'
import Icon from 'src/common/ui-kit/Icon/Icon'
import { NumericFieldMode } from 'src/common/ui-kit/NumericField/types'
import useSmartAccount from 'src/common/api-clients/safe-protocol-kit/useSmartAccount'
import clsx from 'clsx'
import useConfig from 'src/config/hooks/useConfig'

export interface TokenWithAmountSelectorProps {
  title: string
  content?: JSX.Element | null
  token: AnyToken | undefined
  amount: Big
  amountControl?: boolean
  onTokenChange?: (value: AnyToken) => void
  onTokenAmountChange?: (value: Big) => void
  excludeToken?: AnyToken
  excludeNFT?: boolean
  disabled?: boolean
  readonly?: boolean
  enableNFTBatching?: boolean
  nftGroup?: boolean
  viewBalance?: boolean
  className?: string
  onlyOwned?: boolean
}

const TokenWithAmountSelector = ({
  title,
  content = null,
  token,
  amount,
  amountControl,
  onTokenChange,
  onTokenAmountChange,
  excludeToken,
  excludeNFT,
  disabled,
  readonly,
  enableNFTBatching,
  nftGroup,
  viewBalance = true,
  className,
  onlyOwned,
}: TokenWithAmountSelectorProps) => {
  const config = useConfig()
  const contractAddress = token?.contractAddress

  const handleSelectToken = (newToken: AnyToken) => {
    onTokenChange?.(newToken)
  }
  const handleAmountChange = (newAmount: Big) => {
    if (readonly) return
    onTokenAmountChange?.(newAmount)
  }

  const { smartAccountInfo } = useSmartAccount()

  const { priceFeeds } = useAllPriceFeeds()

  const tokenUsdPrice = getPriceFromPriceFeed(priceFeeds, contractAddress) || 0
  const amountUsd = amount.mul(tokenUsdPrice)
  const displayedAmountUsd = displayAmount(amountUsd, {
    maxDecimals: amountUsd.gte(10) ? 0 : 2,
  })

  const mode: NumericFieldMode = token?.type === 'ERC20' ? 'float' : 'integer'
  const maxValue =
    config.tokensAddresses.vicc === contractAddress
      ? Math.min(token?.balance?.toNumber() || 0, 100)
      : enableNFTBatching || token?.type !== 'ERC1155'
      ? undefined
      : token.balance || 1

  return (
    <div
      className={clsx('paper p-4 col-span-1 flex flex-col gap-2', className)}
    >
      <div className='typo-label2'>{title}</div>
      <div className='grid grid-cols-12 items-center'>
        <div className='col-span-4'>
          <TokenSelector
            value={token}
            onChange={handleSelectToken}
            excludeToken={excludeToken}
            excludeNFT={excludeNFT}
            readonly={readonly}
            nftGroup={nftGroup}
            onlyOwned={onlyOwned}
            className='rounded-e-none min-h-11'
          />
        </div>
        <NumericField
          type='big'
          className='col-start-5 col-span-8 pr-0'
          mode={mode}
          value={amount}
          maxValue={maxValue}
          maxDecimals={token?.decimals}
          onChange={handleAmountChange}
          disabled={disabled}
          variant='text'
          size='lg'
          align='right'
          hideError
          inputProps={{
            placeholder: '0',
            className: clsx(
              'rounded-e-md min-h-11',
              readonly && 'pointer-events-none'
            ),
          }}
        />
      </div>
      <div className='flex justify-between'>
        {viewBalance ? (
          <>
            {amountControl ? (
              <TokenAmountController
                token={token}
                onChange={handleAmountChange}
                value={amount}
                disabled={!token}
                max={maxValue ? Big(maxValue) : undefined}
              />
            ) : (
              <TokenBalance
                token={token}
                ownerAddress={smartAccountInfo?.address}
                StartAdornment={<Icon width={14} name='wallet' />}
              />
            )}
          </>
        ) : (
          <div></div>
        )}
        {Boolean(tokenUsdPrice) && (
          <div>
            <div className='typo-label2'>${displayedAmountUsd}</div>
          </div>
        )}
      </div>
      {content}
    </div>
  )
}

export default TokenWithAmountSelector
